import { useEffect, useRef } from 'react';

const DEFAULT_TIMEOUT_MS = 5000;

export interface UseAutoDismissFeedbackOptions {
  error: string | null;
  success: string | null;
  /**
   * Set when `error` describes a PERSISTENT condition (a 403 permission
   * denial, in practice — see `isForbiddenError`) rather than a transient
   * one. A persistent error is never auto-dismissed: it stays on screen
   * until the user dismisses it or navigates away. A `success` message
   * present at the same time is still auto-dismissed normally.
   *
   * Defaults to `false` (existing "always auto-dismiss" behavior).
   */
  isErrorPersistent?: boolean;
  onDismissError: () => void;
  onDismissSuccess: () => void;
  timeoutMs?: number;
}

/**
 * Auto-dismisses transient success/error banner feedback after `timeoutMs`.
 *
 * Extracted from the `FEEDBACK_TIMEOUT_MS` effect duplicated across every
 * admin list page (EventsPage, UsersPage, OrganizationsPage, ZonesIndexPage,
 * ...) so the "never auto-dismiss a persistent/permission error" fix lives
 * in one place instead of being copy-pasted per page. See the production
 * incident this fixes: a 403 caused by a stale-role token was being cleared
 * from the screen 5 seconds after it appeared, leaving the user looking at
 * an unexplained empty list.
 */
export function useAutoDismissFeedback({
  error,
  success,
  isErrorPersistent = false,
  onDismissError,
  onDismissSuccess,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseAutoDismissFeedbackOptions): void {
  // Every call site passes these as inline arrows, so their identity changes
  // on every render. Kept in a ref (rather than listed as effect deps) so a
  // re-render during the countdown does NOT restart the timer — that would
  // silently keep a banner on screen forever on any page that re-renders
  // while it is showing.
  const callbacks = useRef({ onDismissError, onDismissSuccess });
  callbacks.current = { onDismissError, onDismissSuccess };

  useEffect(() => {
    if (!success && !error) return;

    if (error && isErrorPersistent) {
      // The error stays until the user dismisses it or navigates away. A
      // lingering success message (rare alongside a persistent error) is
      // still auto-dismissed on its own.
      if (!success) return;
      const id = window.setTimeout(() => callbacks.current.onDismissSuccess(), timeoutMs);
      return () => window.clearTimeout(id);
    }

    const id = window.setTimeout(() => {
      callbacks.current.onDismissSuccess();
      callbacks.current.onDismissError();
    }, timeoutMs);
    return () => window.clearTimeout(id);
  }, [success, error, isErrorPersistent, timeoutMs]);
}
